<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { api } from "@/api/client";
import { describeError } from "@/utils/errors";
import type {
  AgentView,
  EnrollTokenView,
  McpCatalogEntry,
  McpConfigView,
  UpdateChannel,
  UpdatePolicy,
} from "@/api/types";
import SecretReveal from "@/components/SecretReveal.vue";
import AgentFilterBar from "@/components/AgentFilterBar.vue";
import TagEditor from "@/components/TagEditor.vue";
import { useAgentFilter } from "@/composables/useAgentFilter";
import { lastSeenLabel } from "@/utils/lastSeen";

const { t, locale } = useI18n();

const items = ref<AgentView[]>([]);
const loading = ref(true);
const listError = ref<string | null>(null);
const filter = useAgentFilter(items);

const newLogin = ref("");
const newHostname = ref("");
const newDisplay = ref("");
const formError = ref<string | null>(null);
const submitting = ref(false);

// Plain-text password returned by POST /api/agents — shown once.
const lastSecret = ref<{ password: string; login: string } | null>(null);

// --- agent enrollment (join) tokens --------------------------------
const enrollTokens = ref<EnrollTokenView[]>([]);
// Only live tokens by default; expired/used/revoked stay hidden unless asked
// (the server drops them for good a week after expiry).
const showDeadTokens = ref(false);
const visibleTokens = computed(() =>
  showDeadTokens.value
    ? enrollTokens.value
    : enrollTokens.value.filter((tk) => tk.status === "active"),
);
const deadTokenCount = computed(
  () => enrollTokens.value.filter((tk) => tk.status !== "active").length,
);
const enrollError = ref<string | null>(null);
const etMaxUses = ref(1);
const etTtlHours = ref(1);
const etRelay = ref(true);
const etHostnamePattern = ref("");
const etIpAllowlist = ref("");
const etTagsList = ref<string[]>([]);
const etSubmitting = ref(false);
// The ready-to-run enroll command (carries the plaintext token) — shown once.
const lastEnrollCommand = ref<string | null>(null);
// The bare token — what the Windows installer asks for.
const lastEnrollToken = ref<string | null>(null);

function enrollCommand(token: string): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `nythronix-agent enroll --token ${token} --url ${proto}://${window.location.host}/agent/ws`;
}

async function loadEnrollTokens() {
  enrollError.value = null;
  try {
    enrollTokens.value = (await api.agents.enrollTokens.list()).enroll_tokens;
  } catch (err) {
    enrollError.value = errorMessage(err);
  }
}

async function createEnrollToken() {
  enrollError.value = null;
  etSubmitting.value = true;
  try {
    const ips = etIpAllowlist.value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const created = await api.agents.enrollTokens.create({
      max_uses: etMaxUses.value,
      ttl_secs: Math.round(etTtlHours.value * 3600),
      relay_enabled: etRelay.value,
      hostname_pattern: etHostnamePattern.value.trim() || undefined,
      ip_allowlist: ips.length ? ips : undefined,
      tags: etTagsList.value,
    });
    lastEnrollCommand.value = enrollCommand(created.value);
    lastEnrollToken.value = created.value;
    etHostnamePattern.value = "";
    etIpAllowlist.value = "";
    await loadEnrollTokens();
  } catch (err) {
    enrollError.value = errorMessage(err);
  } finally {
    etSubmitting.value = false;
  }
}

async function revokeEnrollToken(id: string) {
  if (!window.confirm(t("admin.agents.enroll_revoke_confirm"))) return;
  try {
    await api.agents.enrollTokens.revoke(id);
    await loadEnrollTokens();
  } catch (err) {
    enrollError.value = errorMessage(err);
  }
}

// `silent` refreshes (after a toggle) update the rows in place WITHOUT flipping
// `loading`, so the table doesn't collapse to a placeholder and jump. Only the
// initial mount shows the loading state.
async function reload(silent = false) {
  if (!silent) loading.value = true;
  listError.value = null;
  try {
    const resp = await api.agents.list();
    items.value = resp.agents;
  } catch (err) {
    listError.value = errorMessage(err);
  } finally {
    if (!silent) loading.value = false;
  }
}

async function submit() {
  formError.value = null;
  submitting.value = true;
  try {
    const created = await api.agents.create({
      login: newLogin.value.trim(),
      hostname: newHostname.value.trim(),
      display_name: newDisplay.value.trim() || undefined,
    });
    lastSecret.value = { password: created.password, login: created.login };
    newLogin.value = "";
    newHostname.value = "";
    newDisplay.value = "";
    await reload(true);
  } catch (err) {
    formError.value = errorMessage(err);
  } finally {
    submitting.value = false;
  }
}

async function toggleStatus(a: AgentView) {
  try {
    await api.agents.update(a.id, {
      status: a.status === "enabled" ? "disabled" : "enabled",
    });
    await reload(true);
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

async function toggleRelay(a: AgentView) {
  try {
    await api.agents.update(a.id, { relay_enabled: !a.relay_enabled });
    await reload(true);
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

async function setAccessMode(a: AgentView, ev: Event) {
  const sel = ev.target as HTMLSelectElement;
  const mode = sel.value as "managed" | "consent-only";
  if (mode === a.access_mode) return;
  try {
    await api.agents.update(a.id, { access_mode: mode });
    await reload(true);
  } catch (err) {
    listError.value = errorMessage(err);
    sel.value = a.access_mode ?? "managed";
  }
}

// --- worker sudo policy (non-root agent, JIT root) --------------------
// Granting `all` = passwordless root on that machine, so it's confirmed via a
// card with a mandatory expiry (JIT). Revoke is one click. A 1s tick drives the
// live countdown.
const SUDO_DURATIONS = [
  { secs: 900, key: "15m" },
  { secs: 3600, key: "1h" },
  { secs: 14400, key: "4h" },
  { secs: 28800, key: "8h" },
];
const sudoGrantFor = ref<AgentView | null>(null);
const sudoModalRef = ref<HTMLElement>();
const sudoTtl = ref(3600);
const sudoBusy = ref(false);
const nowTs = ref(Date.now());
let sudoTicker: ReturnType<typeof setInterval> | undefined;

function openSudoGrant(a: AgentView) {
  sudoTtl.value = 3600;
  sudoGrantFor.value = a;
  // Move focus into the dialog so Esc/keyboard work and the reader announces it.
  void nextTick(() => sudoModalRef.value?.focus());
}
function sudoExpiryPreview(): string {
  return new Date(nowTs.value + sudoTtl.value * 1000).toLocaleTimeString();
}
function sudoCountdown(a: AgentView): string {
  if (!a.sudo_expires_at) return "";
  const ms = new Date(a.sudo_expires_at).getTime() - nowTs.value;
  if (ms <= 0) return "";
  const mins = Math.floor(ms / 60000);
  return mins >= 1
    ? `${mins} ${t("admin.agents.sudo_min")}`
    : `${Math.floor(ms / 1000)} ${t("admin.agents.sudo_sec")}`;
}
async function submitSudoGrant() {
  const a = sudoGrantFor.value;
  if (!a) return;
  sudoBusy.value = true;
  try {
    await api.agents.setSudoPolicy(a.id, { policy: "all", ttl_secs: sudoTtl.value });
    sudoGrantFor.value = null;
    await reload(true);
  } catch (err) {
    listError.value = errorMessage(err);
  } finally {
    sudoBusy.value = false;
  }
}
async function revokeSudo(a: AgentView) {
  try {
    await api.agents.setSudoPolicy(a.id, { policy: "none" });
    await reload(true);
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

// --- MCP skills (§skills-via-MCP S3c) --------------------------------
// Enable/disable a machine's skill servers from the CP. We show the machine's
// own catalog (advertised names + params + read-only) and toggle which are in
// the desired set; the agent maps each name to a vetted command locally.
const skillsFor = ref<AgentView | null>(null);
const skillsModalRef = ref<HTMLElement>();
const skillsCfg = ref<McpConfigView | null>(null);
const skillsBusy = ref(false);
const skillsError = ref<string | null>(null);
const skillsDraft = ref<Record<string, { enabled: boolean; params: Record<string, string> }>>({});

async function openSkills(a: AgentView) {
  skillsFor.value = a;
  skillsCfg.value = null;
  skillsError.value = null;
  skillsDraft.value = {};
  void nextTick(() => skillsModalRef.value?.focus());
  try {
    const cfg = await api.agents.getMcpConfig(a.id);
    skillsCfg.value = cfg;
    const draft: Record<string, { enabled: boolean; params: Record<string, string> }> = {};
    const names = new Set<string>([
      ...cfg.catalog.map((c) => c.name),
      ...cfg.desired.map((d) => d.name),
    ]);
    for (const name of names) {
      const desired = cfg.desired.find((d) => d.name === name);
      const cat = cfg.catalog.find((c) => c.name === name);
      const params: Record<string, string> = {};
      for (const p of cat?.params ?? Object.keys(desired?.params ?? {})) {
        params[p] = desired?.params?.[p] ?? "";
      }
      draft[name] = { enabled: !!desired, params };
    }
    skillsDraft.value = draft;
  } catch (err) {
    skillsError.value = errorMessage(err);
  }
}
function skillCatalogEntry(name: string): McpCatalogEntry | undefined {
  return skillsCfg.value?.catalog.find((c) => c.name === name);
}
function skillIsRunning(name: string): boolean {
  return !!skillsCfg.value?.running.some((r) => r.name === name);
}
function skillRowNames(): string[] {
  return Object.keys(skillsDraft.value).sort();
}
async function submitSkills() {
  const a = skillsFor.value;
  if (!a) return;
  skillsBusy.value = true;
  skillsError.value = null;
  try {
    const servers = Object.entries(skillsDraft.value)
      .filter(([, v]) => v.enabled)
      .map(([name, v]) => ({ name, params: v.params }));
    await api.agents.setMcpConfig(a.id, servers);
    skillsFor.value = null;
  } catch (err) {
    skillsError.value = errorMessage(err);
  } finally {
    skillsBusy.value = false;
  }
}

async function setChannel(a: AgentView, ev: Event) {
  const sel = ev.target as HTMLSelectElement;
  const channel = sel.value as UpdateChannel;
  if (channel === a.update_channel) return;
  try {
    await api.agents.update(a.id, { update_channel: channel });
    await reload(true);
  } catch (err) {
    listError.value = errorMessage(err);
    // §F12: snap the DOM back to the canonical value — Vue's :value
    // bind won't re-render because `a.update_channel` didn't change.
    sel.value = a.update_channel;
  }
}

async function setPolicy(a: AgentView, ev: Event) {
  const sel = ev.target as HTMLSelectElement;
  const policy = sel.value as UpdatePolicy;
  if (policy === a.update_policy) return;
  try {
    await api.agents.update(a.id, { update_policy: policy });
    await reload(true);
  } catch (err) {
    listError.value = errorMessage(err);
    sel.value = a.update_policy;
  }
}

async function remove(a: AgentView) {
  if (!window.confirm(t("admin.confirm.delete_agent", { login: a.login }))) return;
  try {
    await api.agents.delete(a.id);
    await reload(true);
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

async function rename(a: AgentView) {
  const next = window.prompt(
    t("admin.agents.rename_prompt", { login: a.login }),
    a.display_name ?? "",
  );
  if (next === null) return; // cancelled
  const display_name = next.trim();
  try {
    await api.agents.update(a.id, { display_name });
    await reload(true);
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

// --- tag editor modal ---------------------------------------------
const tagsFor = ref<AgentView | null>(null);
const tagsDraft = ref<string[]>([]);
const tagsBusy = ref(false);
const tagsModalRef = ref<HTMLElement>();
/** Every tag in use across agents and enroll tokens — the picker's suggestions. */
const allKnownTags = computed(() => {
  const set = new Set<string>();
  for (const a of items.value) for (const tg of a.tags ?? []) set.add(tg);
  for (const tk of enrollTokens.value) for (const tg of tk.tags ?? []) set.add(tg);
  return [...set].sort();
});
function editTags(a: AgentView) {
  tagsFor.value = a;
  tagsDraft.value = [...(a.tags ?? [])];
  void nextTick(() => tagsModalRef.value?.focus());
}
async function submitTags() {
  const a = tagsFor.value;
  if (!a) return;
  tagsBusy.value = true;
  try {
    await api.agents.update(a.id, { tags: tagsDraft.value });
    tagsFor.value = null;
    await reload(true);
  } catch (err) {
    listError.value = errorMessage(err);
  } finally {
    tagsBusy.value = false;
  }
}

/** Human-readable platform: OS + arch, or "—" when offline. */
function platformLabel(a: AgentView): string {
  if (!a.os) return "—";
  return a.arch ? `${a.os} · ${a.arch}` : a.os;
}

/** Agents with a live shell capability can host a dashboard terminal; the rest
 * (Win7/Server 2008 R2 relay-only, or offline) are SSH-relay only. */
function terminalCapable(a: AgentView): boolean {
  return (a.capabilities ?? []).includes("shell");
}

function errorMessage(err: unknown): string {
  return describeError(err, t);
}

onMounted(() => {
  reload();
  loadEnrollTokens();
  sudoTicker = setInterval(() => (nowTs.value = Date.now()), 1000);
});
onUnmounted(() => {
  if (sudoTicker) clearInterval(sudoTicker);
});
</script>

<template>
  <div class="admin-page">
    <div class="admin-page__header">
      <h1>{{ t("admin.nav.agents") }}</h1>
    </div>
    <AgentFilterBar :filter="filter" />

    <SecretReveal
      v-if="lastSecret"
      :secret="lastSecret.password"
      :label="t('admin.agents.password_shown_once', { login: lastSecret.login })"
      @dismiss="lastSecret = null"
    />

    <section class="admin-card">
      <h2 style="margin-top: 0; font-size: 1rem">{{ t("admin.agents.enroll_title") }}</h2>
      <p style="margin: 0 0 0.75rem; opacity: 0.8; font-size: 0.85rem">
        {{ t("admin.agents.enroll_hint") }}
      </p>
      <p style="margin: 0 0 0.75rem; font-size: 0.85rem">
        <a href="/downloads/nythronix-agent-setup.exe" data-test="download-windows">
          ⬇ {{ t("admin.agents.download_windows") }}
        </a>
        <span style="opacity: 0.7"> — {{ t("admin.agents.download_hint") }}</span>
      </p>

      <SecretReveal
        v-if="lastEnrollToken"
        :secret="lastEnrollToken"
        :label="t('admin.agents.enroll_token_shown_once')"
        data-test="enroll-token-reveal"
        @dismiss="lastEnrollToken = null"
      />
      <SecretReveal
        v-if="lastEnrollCommand"
        :secret="lastEnrollCommand"
        :label="t('admin.agents.enroll_shown_once')"
        @dismiss="lastEnrollCommand = null"
      />

      <form class="admin-form" @submit.prevent="createEnrollToken">
        <label>
          <span>{{ t("admin.agents.enroll_max_uses") }}</span>
          <input v-model.number="etMaxUses" type="number" min="1" :disabled="etSubmitting" />
        </label>
        <label>
          <span>{{ t("admin.agents.enroll_ttl_hours") }}</span>
          <input
            v-model.number="etTtlHours"
            type="number"
            min="0.1"
            step="0.1"
            :disabled="etSubmitting"
          />
        </label>
        <label>
          <span>{{ t("admin.agents.enroll_hostname_pattern") }}</span>
          <input v-model="etHostnamePattern" placeholder="web-*.corp" :disabled="etSubmitting" />
        </label>
        <label>
          <span>{{ t("admin.agents.enroll_ip_allowlist") }}</span>
          <input
            v-model="etIpAllowlist"
            placeholder="10.0.0.0/8, 192.168.1.5"
            :disabled="etSubmitting"
          />
        </label>
        <div class="agents__tags-field">
          <span>{{ t("admin.agents.enroll_tags") }}</span>
          <TagEditor
            v-model="etTagsList"
            :suggestions="allKnownTags"
            :disabled="etSubmitting"
            input-test-id="enroll-tags"
          />
        </div>
        <label class="agents__relay">
          <input v-model="etRelay" type="checkbox" :disabled="etSubmitting" />
          <span>{{ t("admin.agents.enroll_relay_enabled") }}</span>
        </label>
        <button type="submit" class="admin-form__submit" :disabled="etSubmitting">
          {{ etSubmitting ? t("admin.submitting") : t("admin.agents.enroll_generate") }}
        </button>
      </form>
      <p v-if="enrollError" class="admin-error" style="margin-top: 0.5rem">{{ enrollError }}</p>

      <label v-if="deadTokenCount" class="agents__relay" style="margin-top: 1rem">
        <input v-model="showDeadTokens" type="checkbox" data-test="enroll-show-dead" />
        <span>{{ t("admin.agents.enroll_show_dead", { n: deadTokenCount }) }}</span>
      </label>
      <div v-if="visibleTokens.length" class="admin-table-wrap" style="margin-top: 1rem">
        <table class="admin-table">
          <thead>
            <tr>
              <th>{{ t("admin.status") }}</th>
              <th>{{ t("admin.agents.enroll_uses") }}</th>
              <th>{{ t("admin.agents.enroll_expires") }}</th>
              <th>{{ t("admin.agents.relay") }}</th>
              <th>{{ t("admin.agents.enroll_hostname_pattern") }}</th>
              <th>{{ t("admin.agents.enroll_ip_allowlist") }}</th>
              <th>{{ t("admin.agents.tags") }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tk in visibleTokens" :key="tk.id" :data-test="`token-row-${tk.id}`">
              <td :data-label="t('admin.status')">
                <span
                  class="admin-badge"
                  :data-tone="tk.status === 'active' ? 'ok' : tk.status === 'used' ? 'info' : 'bad'"
                  :data-test="`token-status-${tk.id}`"
                >
                  {{ t(`admin.agents.enroll_status_${tk.status}`) }}
                </span>
              </td>
              <td :data-label="t('admin.agents.enroll_uses')">
                {{ tk.used_count }} / {{ tk.max_uses }}
              </td>
              <td :data-label="t('admin.agents.enroll_expires')">
                {{ tk.expires_at.slice(0, 16).replace("T", " ") }}
              </td>
              <td :data-label="t('admin.agents.relay')">{{ tk.relay_enabled ? "✓" : "—" }}</td>
              <td :data-label="t('admin.agents.enroll_hostname_pattern')">
                {{ tk.hostname_pattern ?? "—" }}
              </td>
              <td :data-label="t('admin.agents.enroll_ip_allowlist')">
                {{ tk.ip_allowlist.length ? tk.ip_allowlist.join(", ") : "—" }}
              </td>
              <td :data-label="t('admin.agents.tags')" :data-test="`token-tags-${tk.id}`">
                {{ tk.tags?.length ? tk.tags.join(" ") : "—" }}
              </td>
              <td class="admin-table__actions">
                <button
                  v-if="tk.status === 'active'"
                  type="button"
                  @click="revokeEnrollToken(tk.id)"
                >
                  {{ t("admin.agents.enroll_revoke") }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="admin-card">
      <p v-if="loading" class="admin-empty">{{ t("admin.loading") }}</p>
      <p v-else-if="listError" class="admin-error">{{ listError }}</p>
      <p v-else-if="filter.filtered.length === 0" class="admin-empty">
        {{ t("admin.no_results") }}
      </p>
      <div v-else class="admin-table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>{{ t("admin.agents.machine") }}</th>
              <th>{{ t("admin.agents.tags") }}</th>
              <th>{{ t("admin.agents.platform") }}</th>
              <th>{{ t("admin.agents.version") }}</th>
              <th>{{ t("admin.presence") }}</th>
              <th>{{ t("admin.status") }}</th>
              <th>{{ t("admin.update.channel") }}</th>
              <th>{{ t("admin.update.policy") }}</th>
              <th>{{ t("admin.agents.relay") }}</th>
              <th>{{ t("admin.agents.access_mode") }}</th>
              <th>{{ t("admin.agents.sudo") }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in filter.filtered" :key="a.id" :data-test="`agent-row-${a.id}`">
              <td :data-label="t('admin.agents.machine')">
                <strong>{{ a.display_name || a.hostname }}</strong>
                <div class="agents__sub">{{ a.login }} · {{ a.hostname }}</div>
              </td>
              <td :data-label="t('admin.agents.tags')">
                <div class="tags" :data-test="`tags-${a.id}`">
                  <button
                    v-for="tag in a.tags ?? []"
                    :key="tag"
                    type="button"
                    class="tag"
                    :data-active="filter.tags.includes(tag) ? 'true' : undefined"
                    @click="filter.toggleTag(tag)"
                  >
                    {{ tag }}
                  </button>
                  <button
                    type="button"
                    class="tag"
                    :data-test="`tags-edit-${a.id}`"
                    @click="editTags(a)"
                  >
                    {{ t("admin.agents.tags_edit") }}
                  </button>
                </div>
              </td>
              <td :data-label="t('admin.agents.platform')">
                <span>{{ platformLabel(a) }}</span>
                <span
                  v-if="a.os && !terminalCapable(a)"
                  class="admin-badge"
                  data-tone="info"
                  :title="t('admin.agents.relay_only_hint')"
                  style="margin-left: 0.35rem"
                  >{{ t("admin.agents.relay_only") }}</span
                >
                <!-- §1 self-describing runtime: the user the worker runs as +
                   whether it can reach Docker (what the AI worker sees). -->
                <div
                  v-if="a.presence === 'online' && (a.runs_as || a.docker_access != null)"
                  class="agents__runtime"
                  :data-test="`agent-runtime-${a.id}`"
                >
                  <span v-if="a.runs_as" :title="t('admin.agents.runs_as_hint')"
                    >{{ t("admin.agents.runs_as") }} {{ a.runs_as }}</span
                  >
                  <span
                    v-if="a.docker_access != null"
                    class="agents__docker"
                    :data-ok="a.docker_access"
                    :title="t('admin.agents.docker_hint')"
                    >docker {{ a.docker_access ? "✓" : "✗" }}</span
                  >
                </div>
              </td>
              <td :data-label="t('admin.agents.version')">{{ a.version ?? "—" }}</td>
              <td :data-label="t('admin.presence')">
                <span class="admin-badge" :data-tone="a.presence === 'online' ? 'ok' : 'bad'">{{
                  a.presence === "online" ? t("home.online") : t("home.offline")
                }}</span>
                <small
                  v-if="a.presence !== 'online' && lastSeenLabel(a.last_seen_at, locale)"
                  class="admin-hint"
                  >{{ t("home.last_seen", { rel: lastSeenLabel(a.last_seen_at, locale) }) }}</small
                >
              </td>
              <td :data-label="t('admin.status')">
                <span class="admin-badge" :data-tone="a.status === 'enabled' ? 'ok' : 'bad'">{{
                  t(`admin.status_${a.status}`)
                }}</span>
              </td>
              <td :data-label="t('admin.update.channel')">
                <select :value="a.update_channel" @change="setChannel(a, $event)">
                  <option value="canary">canary</option>
                  <option value="stable">stable</option>
                </select>
              </td>
              <td :data-label="t('admin.update.policy')">
                <select :value="a.update_policy" @change="setPolicy(a, $event)">
                  <option value="manual">manual</option>
                  <option value="disabled">disabled</option>
                  <option value="auto">auto</option>
                  <option value="auto_idle_only">auto_idle_only</option>
                </select>
              </td>
              <td :data-label="t('admin.agents.relay')">
                <label class="agents__relay" :title="t('admin.agents.relay_hint')">
                  <input type="checkbox" :checked="a.relay_enabled" @change="toggleRelay(a)" />
                  <span class="admin-badge" :data-tone="a.relay_enabled ? 'ok' : 'bad'">{{
                    a.relay_enabled ? t("admin.relay_on") : t("admin.relay_off")
                  }}</span>
                </label>
              </td>
              <td :data-label="t('admin.agents.access_mode')">
                <select :value="a.access_mode ?? 'managed'" @change="setAccessMode(a, $event)">
                  <option value="managed">{{ t("admin.agents.managed") }}</option>
                  <option value="consent-only">{{ t("admin.agents.consent_only") }}</option>
                </select>
              </td>
              <td :data-label="t('admin.agents.sudo')">
                <template v-if="(a.sudo_policy ?? 'none') === 'none'">
                  <span
                    class="admin-badge"
                    data-tone="ok"
                    :title="t('admin.agents.sudo_none_hint')"
                    :data-test="`sudo-badge-${a.id}`"
                    >{{ t("admin.agents.sudo_none") }}</span
                  >
                  <button
                    type="button"
                    class="agents__sudo-btn"
                    :data-test="`sudo-grant-${a.id}`"
                    @click="openSudoGrant(a)"
                  >
                    {{ t("admin.agents.sudo_grant") }}
                  </button>
                </template>
                <template v-else>
                  <span class="admin-badge" data-tone="bad" :data-test="`sudo-badge-${a.id}`">{{
                    a.sudo_policy === "all"
                      ? t("admin.agents.sudo_root")
                      : t("admin.agents.sudo_list")
                  }}</span>
                  <span
                    v-if="sudoCountdown(a)"
                    class="agents__sudo-cd"
                    :data-test="`sudo-countdown-${a.id}`"
                    >· {{ sudoCountdown(a) }}</span
                  >
                  <button
                    type="button"
                    class="agents__sudo-btn"
                    :data-test="`sudo-revoke-${a.id}`"
                    @click="revokeSudo(a)"
                  >
                    {{ t("admin.agents.sudo_revoke") }}
                  </button>
                </template>
              </td>
              <td class="admin-table__actions">
                <button type="button" @click="rename(a)">{{ t("admin.agents.rename") }}</button>
                <button type="button" :data-test="`skills-${a.id}`" @click="openSkills(a)">
                  {{ t("admin.agents.skills_manage") }}
                </button>
                <button type="button" @click="toggleStatus(a)">
                  {{ a.status === "enabled" ? t("admin.disable") : t("admin.enable") }}
                </button>
                <button type="button" :data-test="`delete-${a.id}`" @click="remove(a)">
                  {{ t("admin.delete") }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <details class="admin-card agents__manual" data-test="manual-create">
      <summary>{{ t("admin.agents.manual_title") }}</summary>
      <p style="margin: 0.5rem 0 0.75rem; opacity: 0.8; font-size: 0.85rem">
        {{ t("admin.agents.manual_hint") }}
      </p>
      <form class="admin-form" @submit.prevent="submit">
        <label>
          <span>{{ t("admin.agents.login") }}</span>
          <input v-model="newLogin" required :disabled="submitting" />
        </label>
        <label>
          <span>{{ t("admin.agents.hostname") }}</span>
          <input v-model="newHostname" required :disabled="submitting" />
        </label>
        <label>
          <span>{{ t("admin.agents.display_name") }}</span>
          <input v-model="newDisplay" :disabled="submitting" />
        </label>
        <button type="submit" class="admin-form__submit" :disabled="submitting">
          {{ submitting ? t("admin.submitting") : t("admin.create") }}
        </button>
      </form>
      <p v-if="formError" class="admin-error" style="margin-top: 0.5rem">{{ formError }}</p>
    </details>

    <!-- Tag editor: pick from tags already in use or type new ones. -->
    <div
      v-if="tagsFor"
      class="agents__modal-backdrop"
      @click.self="tagsFor = null"
      @keydown.esc="tagsFor = null"
    >
      <div
        ref="tagsModalRef"
        class="agents__modal"
        data-test="tags-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tags-title"
        tabindex="-1"
      >
        <h3 id="tags-title" class="admin-card__title">{{ t("admin.agents.tags_title") }}</h3>
        <p class="agents__modal-machine">
          {{ tagsFor.display_name || tagsFor.hostname }} · {{ tagsFor.login }}
        </p>
        <p class="agents__modal-hint">{{ t("admin.agents.tags_hint") }}</p>
        <TagEditor v-model="tagsDraft" :suggestions="allKnownTags" :disabled="tagsBusy" />
        <div class="agents__modal-actions">
          <button type="button" :disabled="tagsBusy" @click="tagsFor = null">
            {{ t("admin.agents.sudo_cancel") }}
          </button>
          <button type="button" :disabled="tagsBusy" data-test="tags-save" @click="submitTags()">
            {{ t("admin.agents.tags_save") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Grant passwordless root to the worker (JIT): confirmed here because it
         lifts least-privilege on the machine for the chosen window. -->
    <div
      v-if="sudoGrantFor"
      class="agents__modal-backdrop"
      @click.self="sudoGrantFor = null"
      @keydown.esc="sudoGrantFor = null"
    >
      <div
        ref="sudoModalRef"
        class="agents__modal"
        data-test="sudo-grant-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sudo-grant-title"
        tabindex="-1"
      >
        <h3 id="sudo-grant-title" class="admin-card__title">
          {{ t("admin.agents.sudo_grant_title") }}
        </h3>
        <p class="agents__modal-machine">{{ sudoGrantFor.hostname }} · {{ sudoGrantFor.login }}</p>
        <p class="agents__modal-warn">{{ t("admin.agents.sudo_grant_warn") }}</p>
        <div class="agents__ttl">
          <label v-for="d in SUDO_DURATIONS" :key="d.secs">
            <input v-model.number="sudoTtl" type="radio" name="sudottl" :value="d.secs" />
            <span>{{ t(`admin.agents.sudo_dur_${d.key}`) }}</span>
          </label>
        </div>
        <p class="agents__modal-exp">
          {{ t("admin.agents.sudo_expires_preview", { time: sudoExpiryPreview() }) }}
        </p>
        <div class="agents__modal-actions">
          <button type="button" :disabled="sudoBusy" @click="sudoGrantFor = null">
            {{ t("admin.agents.sudo_cancel") }}
          </button>
          <button
            type="button"
            class="agents__sudo-danger"
            :disabled="sudoBusy"
            data-test="sudo-grant-confirm"
            @click="submitSudoGrant()"
          >
            {{ t("admin.agents.sudo_grant_confirm") }}
          </button>
        </div>
      </div>
    </div>

    <!-- MCP skills manager (§skills-via-MCP S3c): toggle which skill servers the
         machine runs. The CP pushes names+params; the machine's catalog maps
         them to vetted commands (catalog caps the CP). -->
    <div
      v-if="skillsFor"
      class="agents__modal-backdrop"
      @click.self="skillsFor = null"
      @keydown.esc="skillsFor = null"
    >
      <div
        ref="skillsModalRef"
        class="agents__modal agents__modal--wide"
        data-test="skills-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="skills-title"
        tabindex="-1"
      >
        <h3 id="skills-title" class="admin-card__title">{{ t("admin.agents.skills_title") }}</h3>
        <p class="agents__modal-machine">{{ skillsFor.hostname }} · {{ skillsFor.login }}</p>
        <p class="agents__modal-hint">{{ t("admin.agents.skills_hint") }}</p>

        <p v-if="skillsError" class="agents__modal-warn" data-test="skills-error">
          {{ skillsError }}
        </p>
        <p
          v-if="skillsCfg && skillsCfg.catalog.length === 0 && skillsCfg.running.length === 0"
          class="agents__modal-hint"
          data-test="skills-offline"
        >
          {{ t("admin.agents.skills_offline") }}
        </p>

        <ul v-if="skillsCfg" class="agents__skills">
          <li
            v-for="name in skillRowNames()"
            :key="name"
            class="agents__skill"
            :data-test="`skill-row-${name}`"
          >
            <label class="agents__skill-head">
              <input
                v-model="skillsDraft[name].enabled"
                type="checkbox"
                :data-test="`skill-toggle-${name}`"
              />
              <span class="agents__skill-name">{{ name }}</span>
              <span v-if="skillCatalogEntry(name)?.read_only" class="admin-badge" data-tone="ok">{{
                t("admin.agents.skills_read_only")
              }}</span>
              <span
                v-if="skillIsRunning(name)"
                class="admin-badge"
                data-tone="ok"
                :data-test="`skill-running-${name}`"
                >{{ t("admin.agents.skills_running") }}</span
              >
              <span v-if="!skillCatalogEntry(name)" class="agents__skill-note">{{
                t("admin.agents.skills_desired_only")
              }}</span>
            </label>
            <div
              v-if="skillsDraft[name].enabled && Object.keys(skillsDraft[name].params).length"
              class="agents__skill-params"
            >
              <label
                v-for="p in Object.keys(skillsDraft[name].params)"
                :key="p"
                class="agents__skill-param"
              >
                <span>{{ p }}</span>
                <input
                  v-model="skillsDraft[name].params[p]"
                  type="text"
                  :data-test="`skill-param-${name}-${p}`"
                  :placeholder="t('admin.agents.skills_param')"
                />
              </label>
            </div>
          </li>
        </ul>
        <p v-else class="agents__modal-hint">…</p>

        <div class="agents__modal-actions">
          <button type="button" :disabled="skillsBusy" @click="skillsFor = null">
            {{ t("admin.agents.skills_cancel") }}
          </button>
          <button
            type="button"
            :disabled="skillsBusy || !skillsCfg"
            data-test="skills-save"
            @click="submitSkills()"
          >
            {{ t("admin.agents.skills_save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.agents__sudo-btn {
  margin-left: 0.4rem;
  font-size: 0.8rem;
  padding: 0.1rem 0.45rem;
}
.agents__runtime {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.2rem;
  font-size: 0.72rem;
  color: var(--color-fg-dim);
  font-family: var(--font-mono);
}
.agents__docker[data-ok="false"] {
  color: var(--color-warn);
}
.agents__docker[data-ok="true"] {
  color: var(--color-ok);
}
.agents__sudo-cd {
  margin-left: 0.35rem;
  font-size: 0.8rem;
  opacity: 0.75;
}
.agents__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.agents__modal {
  background: var(--surface);
  color: inherit;
  border: 1px solid var(--color-border);
  border-radius: 0.6rem;
  padding: 1.25rem;
  max-width: 27rem;
  width: calc(100% - 2rem);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
}
.agents__modal--wide {
  max-width: 34rem;
}
.agents__modal-hint {
  font-size: 0.85rem;
  opacity: 0.75;
  margin: 0 0 0.9rem;
}
.agents__skills {
  list-style: none;
  margin: 0 0 1rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 22rem;
  overflow-y: auto;
}
.agents__skill {
  border: 1px solid var(--color-border);
  border-radius: 0.45rem;
  padding: 0.5rem 0.65rem;
}
.agents__skill-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}
.agents__skill-name {
  font-family: var(--font-mono);
  font-weight: 600;
}
.agents__skill-note {
  font-size: 0.75rem;
  opacity: 0.6;
}
.agents__skill-params {
  margin: 0.55rem 0 0.15rem;
  padding-left: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.agents__skill-param {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}
.agents__skill-param > span {
  font-family: var(--font-mono);
  min-width: 5rem;
  opacity: 0.85;
}
.agents__skill-param > input {
  flex: 1;
}
.agents__modal-machine {
  font-weight: 600;
  margin: 0 0 0.5rem;
}
.agents__modal-warn {
  font-size: 0.88rem;
  opacity: 0.85;
  margin: 0 0 0.9rem;
}
.agents__ttl {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.agents__ttl label {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}
.agents__modal-exp {
  font-size: 0.85rem;
  opacity: 0.7;
  margin: 0 0 1rem;
}
.agents__modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
}
.agents__sudo-danger {
  background: var(--color-danger);
  color: #fff;
  border-color: var(--color-danger);
}
.agents__relay {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  white-space: nowrap;
}
.agents__sub {
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--color-fg-dim);
}
.agents__manual > summary {
  cursor: pointer;
  font-weight: 600;
}
.agents__tags-field {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.85rem;
}
</style>
